import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useNavigate }                from "react-router-dom";
import { useSubscriptionPlans }      from "../../../hooks/useSubscriptionPlans";
import { makeRequest }               from "../../../assets/functions_helper";
import { useActiveSubscription, formatUTCDate, waitForActivation, waitForUpgradeOutcome } from "../../../hooks/useActiveSubscription";
import { useWompiWidget, WIDGET_STATE } from "../../../hooks/useWompiWidget";

// ─── Brand palette ────────────────────────────────────────────────────────────
const RED    = "#F72E41";
const RED_DK = "#c41730";
const NAVY   = "#070724";
const FONT   = "Como, sans-serif";

// ─── Feature label map ────────────────────────────────────────────────────────
const FEATURE_LABELS = {
  analytics:        { label: "Análisis avanzados",             icon: "📊" },
  delivery:         { label: "Pedidos a domicilio (WhatsApp)", icon: "🚀" },
  waiter_calls:     { label: "Llamados a mesero",              icon: "🔔" },
  products_limit:   { label: (v) => `Hasta ${v} productos`,   icon: "🍽️" },
  images_limit:     { label: (v) => `${v} imágenes con IA`,   icon: "🖼️" },
  ai_prompts:       { label: "Mejora de imágenes con IA",      icon: "✨" },
  ai_prompts_limit: { label: (v) => v > 0 ? `${v} mejoras IA / mes` : null, icon: "🤖" },
  public_menu:      { label: "Menú público online",            icon: "🌐" },
  max_restaurants:  { label: (v) => `Hasta ${v} restaurante${v > 1 ? "s" : ""}`, icon: "🏪" },
};

const BILLING_LABEL = { monthly: "mes", yearly: "año", annual: "año" };

const TIER_CONFIG = {
  trial: {
    name: "Prueba", emoji: "🎁",
    cardBg: "#f0f9ff", accentColor: "#0284c7", accentBg: "rgba(2,132,199,0.1)",
    priceShadow: "none", recommended: false,
    headerStrip: `linear-gradient(90deg, #0ea5e9 0%, #0284c7 100%)`,
    buttonBg: `linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)`,
    buttonShadow: "0 4px 14px rgba(2,132,199,0.3)",
    titleColor: "#1E1E1E", textColor: "#334155", subColor: "#64748b",
    dividerColor: "#e2e8f0", badgeBg: "rgba(2,132,199,0.1)", badgeText: "#0369a1", isDark: false,
  },
  basic: {
    name: "Básico", emoji: "🌱",
    cardBg: "#ffffff", accentColor: RED, accentBg: "rgba(247,46,65,0.07)",
    priceShadow: "none", recommended: false,
    headerStrip: `linear-gradient(90deg, ${RED} 0%, #ff6b7a 100%)`,
    buttonBg: `linear-gradient(135deg, ${RED} 0%, ${RED_DK} 100%)`,
    buttonShadow: "0 4px 14px rgba(247,46,65,0.35)",
    titleColor: "#1E1E1E", textColor: "#334155", subColor: "#64748b",
    dividerColor: "#e2e8f0", badgeBg: "rgba(247,46,65,0.1)", badgeText: RED_DK, isDark: false,
  },
  pro: {
    name: "Pro", emoji: "⚡",
    cardBg: NAVY, accentColor: RED, accentBg: "rgba(247,46,65,0.12)",
    priceShadow: "0 0 30px rgba(247,46,65,0.25)", recommended: true,
    headerStrip: `linear-gradient(90deg, ${RED} 0%, #8b0000 100%)`,
    buttonBg: `linear-gradient(135deg, ${RED} 0%, #a81025 100%)`,
    buttonShadow: "0 4px 18px rgba(247,46,65,0.45)",
    titleColor: "#ffffff", textColor: "rgba(255,255,255,0.85)", subColor: "rgba(255,255,255,0.5)",
    dividerColor: "rgba(255,255,255,0.1)", badgeBg: "rgba(247,46,65,0.2)", badgeText: "#ff8a95", isDark: true,
  },
  enterprise: {
    name: "Enterprise", emoji: "🏆",
    cardBg: "#1a1206", accentColor: "#F59E0B", accentBg: "rgba(245,158,11,0.1)",
    priceShadow: "0 0 30px rgba(245,158,11,0.2)", recommended: false,
    headerStrip: "linear-gradient(90deg, #F59E0B 0%, #d97706 100%)",
    buttonBg: "linear-gradient(135deg, #F59E0B 0%, #b45309 100%)",
    buttonShadow: "0 4px 14px rgba(245,158,11,0.4)",
    titleColor: "#ffffff", textColor: "rgba(255,255,255,0.85)", subColor: "rgba(255,255,255,0.5)",
    dividerColor: "rgba(255,255,255,0.1)", badgeBg: "rgba(245,158,11,0.15)", badgeText: "#fde68a", isDark: true,
  },
};

// tier_rank for upgrade/downgrade detection
const TIER_RANK = { trial: 0, basic: 1, pro: 2, enterprise: 3 };

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatPrice(amount, currency) {
  if (amount == null) return "—";
  try {
    return new Intl.NumberFormat("es-CO", {
      style: "currency", currency: currency || "COP",
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toLocaleString("es-CO")}`;
  }
}

function buildFeatureList(features, tier) {
  if (!features) return [];
  // Keys to hide per tier
  const hiddenKeys = tier === "basic" ? ["images_limit"] : [];
  return Object.keys(FEATURE_LABELS).reduce((acc, key) => {
    if (hiddenKeys.includes(key)) return acc;
    const val = features[key];
    const cfg = FEATURE_LABELS[key];
    if (val === false || val === 0 || val === null || val === undefined) return acc;
    const text = typeof cfg.label === "function" ? cfg.label(val) : cfg.label;
    if (!text) return acc;
    acc.push({ icon: cfg.icon, text });
    return acc;
  }, []);
}

const STATUS_LABELS = {
  Active:         { label: "Activa",          color: "#166534", bg: "#dcfce7", border: "#bbf7d0" },
  active:         { label: "Activa",          color: "#166534", bg: "#dcfce7", border: "#bbf7d0" },
  PendingPayment: { label: "Procesando pago", color: "#92400e", bg: "#fef3c7", border: "#fde68a" },
  canceled:       { label: "Cancelada",       color: "#991b1b", bg: "#fee2e2", border: "#fca5a5" },
  past_due:       { label: "Vencida",         color: "#92400e", bg: "#fef3c7", border: "#fde68a" },
  trialing:       { label: "En prueba",       color: "#1e40af", bg: "#dbeafe", border: "#93c5fd" },
  incomplete:     { label: "Incompleta",      color: "#6b21a8", bg: "#f3e8ff", border: "#d8b4fe" },
};

// ─── Active Subscription Panel ────────────────────────────────────────────────
function ActiveSubscriptionPanel({ subscription, isLoading }) {
  if (isLoading) {
    return (
      <>
        <style>{`@keyframes asp-shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
        <div style={{
          maxWidth: "760px", margin: "0 auto 36px",
          borderRadius: "20px", border: "2px solid #e2e8f0",
          overflow: "hidden", background: "#fff",
        }}>
          <div style={{ height: "4px", background: "#e2e8f0" }} />
          <div style={{ padding: "24px 28px", display: "flex", flexDirection: "column", gap: "14px" }}>
            {[100, 60, 40].map((w, i) => (
              <div key={i} style={{
                height: "18px", borderRadius: "8px", width: `${w}%`,
                background: "linear-gradient(90deg,#f0f0f0 25%,#f8f8f8 50%,#f0f0f0 75%)",
                backgroundSize: "200% 100%", animation: "asp-shimmer 1.4s ease infinite",
              }} />
            ))}
          </div>
        </div>
      </>
    );
  }

  if (!subscription) return null;

  const tier      = subscription.plan_tier?.toLowerCase() || "basic";
  const cfg       = TIER_CONFIG[tier] || TIER_CONFIG.basic;
  const statusCfg = STATUS_LABELS[subscription.status] || { label: subscription.status, color: "#475569", bg: "#f8fafc", border: "#e2e8f0" };
  const periodEnd = formatUTCDate(subscription.next_billing_date);
  const cancelDate = formatUTCDate(subscription.cancellation_date);
  const scheduledTier = subscription.scheduled_plan?.plan_tier;
  const scheduledDate = formatUTCDate(subscription.scheduled_plan?.effective_date);
  const scheduledCfg  = TIER_CONFIG[scheduledTier?.toLowerCase()] || null;

  return (
    <div
      style={{
        maxWidth: "760px",
        margin: "0 auto 36px",
        borderRadius: "20px",
        border: `2px solid ${cfg.accentColor}25`,
        overflow: "hidden",
        background: cfg.isDark ? cfg.cardBg : "#ffffff",
        boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
        fontFamily: FONT,
      }}
    >
      {/* Color strip */}
      <div style={{ height: "4px", background: cfg.headerStrip }} />

      <div style={{ padding: "22px 26px" }}>
        {/* Row 1: plan name + status badge */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "14px", flexWrap: "wrap" }}>
          <div style={{
            width: "40px", height: "40px",
            borderRadius: "12px",
            background: cfg.accentBg,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "20px", flexShrink: 0,
          }}>{cfg.emoji}</div>
          <div>
            <div style={{ fontSize: "18px", fontWeight: 900, color: cfg.isDark ? "#fff" : "#0f172a", lineHeight: 1.1 }}>
              Plan {cfg.name}
            </div>
            {periodEnd && (
              <div style={{ fontSize: "12px", color: cfg.isDark ? "rgba(255,255,255,0.5)" : "#64748b", marginTop: "2px" }}>
                Plan activo hasta: {periodEnd}
              </div>
            )}
          </div>
          {/* Status badge */}
          <span style={{
            marginLeft: "auto",
            background: statusCfg.bg,
            color: statusCfg.color,
            border: `1px solid ${statusCfg.border}`,
            borderRadius: "999px",
            fontSize: "11px", fontWeight: 800,
            padding: "4px 12px",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            flexShrink: 0,
          }}>
            {statusCfg.label}
          </span>
          {/* Price */}
          {subscription.amount != null && tier !== "trial" && (
            <span style={{
              fontSize: "16px", fontWeight: 900,
              color: cfg.accentColor,
              letterSpacing: "-0.5px",
              flexShrink: 0,
            }}>
              {(() => {
                try {
                  return new Intl.NumberFormat("es-CO", {
                    style: "currency",
                    currency: subscription.currency || "COP",
                    minimumFractionDigits: 0,
                  }).format(subscription.amount);
                } catch { return `${subscription.currency} ${subscription.amount}`; }
              })()}
              <span style={{ fontSize: "11px", fontWeight: 600, color: cfg.isDark ? "rgba(255,255,255,0.45)" : "#94a3b8", marginLeft: "4px" }}>
                / {BILLING_LABEL[subscription.billing_interval] || "mes"}
              </span>
            </span>
          )}
        </div>

        {/* Cancellation-scheduled banner */}
        {subscription.cancellation_scheduled && (
          <div style={{
            background: "#fff5f5",
            border: "1.5px solid rgba(247,46,65,0.28)",
            borderRadius: "12px",
            padding: "12px 16px",
            marginTop: "4px",
            display: "flex", alignItems: "flex-start", gap: "10px",
          }}>
            <span style={{ fontSize: "18px", flexShrink: 0 }}>🚫</span>
            <p style={{ margin: 0, fontSize: "13px", color: RED_DK, fontWeight: 600, lineHeight: 1.55 }}>
              Tu suscripción ha sido cancelada. Mantendrás el acceso a tu plan actual hasta el{" "}
              <strong>{cancelDate || "final del período"}</strong>.
            </p>
          </div>
        )}

        {/* Scheduled plan banner */}
        {subscription.scheduled_plan && scheduledCfg && (
          <div style={{
            background: "#fffbeb",
            border: "1.5px solid #fde68a",
            borderRadius: "12px",
            padding: "12px 16px",
            marginTop: subscription.cancellation_scheduled ? "10px" : "4px",
            display: "flex", alignItems: "flex-start", gap: "10px",
          }}>
            <span style={{ fontSize: "18px", flexShrink: 0 }}>📅</span>
            <p style={{ margin: 0, fontSize: "13px", color: "#92400e", fontWeight: 600, lineHeight: 1.55 }}>
              Tienes un cambio de plan programado. Tu plan cambiará a{" "}
              <strong>{scheduledCfg.emoji} {scheduledCfg.name}</strong>
              {scheduledDate ? <> el <strong>{scheduledDate}</strong></> : " al finalizar tu ciclo actual"}.
            </p>
          </div>
        )}

        {/* Pending upgrade indicator */}
        {subscription.pending_upgrade_tier && !subscription.last_upgrade_declined && (
          <div style={{
            background: "#f0fdf4",
            border: "1.5px solid #86efac",
            borderRadius: "12px",
            padding: "12px 16px",
            marginTop: "10px",
            display: "flex", alignItems: "center", gap: "10px",
          }}>
            <span style={{
              width: "18px", height: "18px", flexShrink: 0,
              border: "2.5px solid #bbf7d0", borderTopColor: "#16a34a",
              borderRadius: "50%", display: "inline-block",
              animation: "asp-shimmer 0.8s linear infinite",
            }} />
            <p style={{ margin: 0, fontSize: "13px", color: "#15803d", fontWeight: 600, lineHeight: 1.5 }}>
              Procesando upgrade al plan{" "}
              <strong>
                {TIER_CONFIG[subscription.pending_upgrade_tier?.toLowerCase()]?.emoji}{" "}
                {TIER_CONFIG[subscription.pending_upgrade_tier?.toLowerCase()]?.name
                  || subscription.pending_upgrade_tier}
              </strong>…
            </p>
          </div>
        )}
      </div>
    </div>
  );
}


// ─── Confirmation Modal ───────────────────────────────────────────────────────
function ConfirmationModal({ isOpen, onClose, onConfirm, plan, currentTier, isLoading }) {
  if (!isOpen || !plan) return null;

  const cfg = TIER_CONFIG[plan.tier] || TIER_CONFIG.basic;
  const targetRank  = TIER_RANK[plan.tier] || 0;
  const currentRank = TIER_RANK[currentTier?.toLowerCase()] || 0;
  const isUpgrade   = targetRank > currentRank;
  const isFirstTime = !currentTier;

  const upgradeColors = {
    headerBg: `linear-gradient(135deg, ${RED}, ${RED_DK})`,
    iconBg:   `linear-gradient(135deg, ${RED}, ${RED_DK})`,
    accentText: RED,
    confirmBg: `linear-gradient(135deg, ${RED}, ${RED_DK})`,
    confirmShadow: "0 4px 16px rgba(247,46,65,0.4)",
  };
  const downgradeColors = {
    headerBg:  "linear-gradient(135deg, #64748b, #475569)",
    iconBg:    "linear-gradient(135deg, #64748b, #475569)",
    accentText: "#475569",
    confirmBg: "linear-gradient(135deg, #64748b, #475569)",
    confirmShadow: "0 4px 16px rgba(0,0,0,0.18)",
  };
  const colors = (isUpgrade || isFirstTime) ? upgradeColors : downgradeColors;

  return (
    <>
      <style>{`
        @keyframes modal-in {
          0%   { opacity:0; transform:scale(0.92) translateY(12px); }
          100% { opacity:1; transform:scale(1) translateY(0); }
        }
        @keyframes overlay-in { 0%{opacity:0} 100%{opacity:1} }
      `}</style>

      {/* Overlay */}
      <div
        onClick={!isLoading ? onClose : undefined}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(7,7,36,0.55)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          zIndex: 10000,
          animation: "overlay-in 0.2s ease both",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "24px",
        }}
      >
        {/* Modal card */}
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#ffffff",
            borderRadius: "24px",
            width: "100%",
            maxWidth: "440px",
            overflow: "hidden",
            boxShadow: "0 32px 80px rgba(0,0,0,0.22)",
            animation: "modal-in 0.35s cubic-bezier(.34,1.56,.64,1) both",
            fontFamily: FONT,
          }}
        >
          {/* Colored header */}
          <div
            style={{
              background: colors.headerBg,
              padding: "28px 28px 20px",
              textAlign: "center",
              position: "relative",
            }}
          >
            <div
              style={{
                width: "56px", height: "56px",
                borderRadius: "16px",
                background: "rgba(255,255,255,0.15)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "26px",
                margin: "0 auto 12px",
                border: "1.5px solid rgba(255,255,255,0.25)",
              }}
            >
              {(isUpgrade || isFirstTime) ? "🚀" : "⚠️"}
            </div>
            <h2
              style={{
                color: "#ffffff", fontSize: "18px", fontWeight: 800,
                margin: 0, letterSpacing: "-0.3px",
              }}
            >
              {isFirstTime
                ? "¡Activa tu plan!"
                : isUpgrade
                ? "¡Excelente elección!"
                : "¿Confirmar cambio?"}
            </h2>
          </div>

          {/* Body */}
          <div style={{ padding: "24px 28px 28px" }}>
            {/* Plan summary chip */}
            <div
              style={{
                display: "flex", alignItems: "center", gap: "12px",
                background: cfg.isDark ? "#0f172a" : "#f8fafc",
                border: `1.5px solid ${cfg.accentColor}30`,
                borderRadius: "14px",
                padding: "12px 16px",
                marginBottom: "20px",
              }}
            >
              <div
                style={{
                  width: "40px", height: "40px",
                  borderRadius: "10px",
                  background: `${cfg.accentColor}15`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "20px", flexShrink: 0,
                }}
              >
                {cfg.emoji}
              </div>
              <div>
                <div
                  style={{
                    fontSize: "15px", fontWeight: 800,
                    color: cfg.isDark ? "#ffffff" : "#0f172a",
                  }}
                >
                  Plan {cfg.name}
                </div>
                {plan.pricing && (
                  <div style={{ fontSize: "13px", color: cfg.subColor || "#64748b", marginTop: "2px" }}>
                    {formatPrice(plan.pricing.amount, plan.pricing.currency)}{" "}
                    / {BILLING_LABEL[plan.pricing.billing_interval] || "mes"}
                  </div>
                )}
              </div>
              <div
                style={{
                  marginLeft: "auto",
                  background: `${cfg.accentColor}15`,
                  color: cfg.accentColor,
                  fontSize: "11px", fontWeight: 800,
                  padding: "4px 10px", borderRadius: "999px",
                  letterSpacing: "0.05em", textTransform: "uppercase",
                  flexShrink: 0,
                }}
              >
                {plan.tier}
              </div>
            </div>

            {/* Message */}
            <p
              style={{
                color: "#334155", fontSize: "14px", lineHeight: 1.65,
                margin: "0 0 24px", textAlign: "center",
              }}
            >
              {isFirstTime
                ? `¡Estás a un paso de activar tu restaurante! ¿Deseas continuar al pago para el plan ${cfg.name}?`
                : isUpgrade
                ? `¡Estás a un paso de potenciar tu restaurante! ¿Deseas continuar al pago para el plan ${cfg.name}?`
                : `¿Estás seguro de que deseas cambiar al plan ${cfg.name}? Perderás acceso a algunas características premium al finalizar tu ciclo de facturación actual.`}
            </p>

            {/* Downgrade warning banner */}
            {!isUpgrade && !isFirstTime && (
              <div
                style={{
                  background: "#fffbeb",
                  border: "1px solid #fde68a",
                  borderRadius: "10px",
                  padding: "10px 14px",
                  marginBottom: "20px",
                  display: "flex", alignItems: "flex-start", gap: "8px",
                }}
              >
                <span style={{ fontSize: "14px", flexShrink: 0 }}>⚠️</span>
                <p style={{ margin: 0, fontSize: "12px", color: "#92400e", lineHeight: 1.5 }}>
                  El cambio será efectivo al finalizar tu ciclo de facturación actual. Podrás cancelar el downgrade antes de esa fecha.
                </p>
              </div>
            )}

            {/* Action buttons */}
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <button
                onClick={onConfirm}
                disabled={isLoading}
                style={{
                  background: isLoading ? "#e2e8f0" : colors.confirmBg,
                  color: isLoading ? "#94a3b8" : "#ffffff",
                  border: "none",
                  borderRadius: "12px",
                  padding: "13px 24px",
                  fontSize: "14px",
                  fontWeight: 800,
                  fontFamily: FONT,
                  cursor: isLoading ? "not-allowed" : "pointer",
                  boxShadow: isLoading ? "none" : colors.confirmShadow,
                  letterSpacing: "0.04em",
                  transition: "all 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
                onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.filter = "brightness(1.08)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.filter = "brightness(1)"; }}
              >
                {isLoading ? (
                  <>
                    <span
                      style={{
                        width: "16px", height: "16px",
                        border: "2px solid #94a3b8",
                        borderTopColor: "transparent",
                        borderRadius: "50%",
                        display: "inline-block",
                        animation: "spin 0.7s linear infinite",
                      }}
                    />
                    Procesando...
                  </>
                ) : (
                  <>
                    {(isUpgrade || isFirstTime) ? "💳 Continuar al pago" : "✓ Confirmar cambio"}
                  </>
                )}
              </button>

              <button
                onClick={onClose}
                disabled={isLoading}
                style={{
                  background: "transparent",
                  color: "#64748b",
                  border: "2px solid #e2e8f0",
                  borderRadius: "12px",
                  padding: "11px 24px",
                  fontSize: "14px",
                  fontWeight: 700,
                  fontFamily: FONT,
                  cursor: isLoading ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.background = "#f8fafc"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}

// ─── Plan Card ────────────────────────────────────────────────────────────────
function PlanCard({ plan, currentTier, onSelect, cancellationScheduled }) {
  const { tier, pricing, features } = plan;
  const cfg = TIER_CONFIG[tier] || TIER_CONFIG.basic;
  const isCurrentPlan = currentTier?.toLowerCase() === tier;
  // A plan is selectable only when NOT current, NOT during cancellation (unless it IS the current plan),
  // and onSelect is provided (not blocked by isPending).
  const isCancelLocked = cancellationScheduled && !isCurrentPlan;
  const isTrialPlan    = tier === "trial";
  const isDisabled     = isCurrentPlan || isCancelLocked || isTrialPlan || !onSelect;
  const featureList = buildFeatureList(features, tier);
  const billingLabel = BILLING_LABEL[pricing?.billing_interval] || "mes";

  return (
    <div
      style={{
        borderRadius: "20px",
        padding: "2px",
        background: cfg.recommended
          ? `linear-gradient(135deg, ${RED}, #ff6b7a, #870d1c)`
          : tier === "enterprise"
          ? "linear-gradient(135deg, #F59E0B, #d97706)"
          : "#D8D8D8",
        boxShadow: cfg.recommended
          ? `0 20px 60px rgba(247,46,65,0.2)`
          : "0 4px 24px rgba(0,0,0,0.08)",
        position: "relative",
        transition: "transform 0.3s cubic-bezier(.34,1.56,.64,1), box-shadow 0.3s ease",
        height: "100%",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-8px)";
        e.currentTarget.style.boxShadow = cfg.recommended
          ? "0 28px 70px rgba(247,46,65,0.28)"
          : tier === "enterprise"
          ? "0 24px 60px rgba(245,158,11,0.22)"
          : "0 16px 48px rgba(0,0,0,0.14)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = cfg.recommended
          ? "0 20px 60px rgba(247,46,65,0.2)"
          : tier === "enterprise"
          ? "0 8px 32px rgba(245,158,11,0.1)"
          : "0 4px 24px rgba(0,0,0,0.08)";
      }}
    >
      {cfg.recommended && (
        <div
          style={{
            position: "absolute", top: "-14px", left: "50%",
            transform: "translateX(-50%)",
            background: `linear-gradient(135deg, ${RED}, ${RED_DK})`,
            color: "white", fontSize: "10px", fontWeight: 800,
            padding: "5px 20px", borderRadius: "999px",
            letterSpacing: "0.1em", textTransform: "uppercase",
            whiteSpace: "nowrap",
            boxShadow: "0 4px 16px rgba(247,46,65,0.45)", zIndex: 2,
            fontFamily: FONT,
          }}
        >
          ⭐ Más popular
        </div>
      )}

      <div
        style={{
          background: cfg.cardBg, borderRadius: "18px", overflow: "hidden",
          display: "flex", flexDirection: "column", height: "100%", boxSizing: "border-box",
        }}
      >
        <div style={{ height: "4px", background: cfg.headerStrip, flexShrink: 0 }} />

        <div style={{ padding: "28px 26px 26px", display: "flex", flexDirection: "column", flex: 1 }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "22px" }}>
            <div
              style={{
                width: "40px", height: "40px", borderRadius: "12px",
                background: cfg.accentBg,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "20px", flexShrink: 0,
              }}
            >
              {cfg.emoji}
            </div>
            <div style={{ flex: 1, fontSize: "18px", fontWeight: 800, color: cfg.titleColor, fontFamily: FONT }}>
              {cfg.name}
            </div>
            {isCurrentPlan && (
              <span
                style={{
                  background: cfg.badgeBg, color: cfg.badgeText,
                  fontSize: "9px", fontWeight: 800,
                  padding: "4px 10px", borderRadius: "999px",
                  letterSpacing: "0.08em", textTransform: "uppercase",
                  flexShrink: 0, fontFamily: FONT,
                  border: `1px solid ${cfg.accentColor}40`,
                }}
              >
                Plan Actual
              </span>
            )}
          </div>

          {/* Price */}
          <div style={{ marginBottom: "24px" }}>
            {pricing ? (
              <>
                <div
                  style={{
                    fontSize: "40px", fontWeight: 900,
                    color: cfg.accentColor, lineHeight: 1,
                    fontFamily: FONT, letterSpacing: "-1.5px",
                    textShadow: cfg.priceShadow,
                  }}
                >
                  {formatPrice(pricing.amount, pricing.currency)}
                </div>
                <div style={{ color: cfg.subColor, fontSize: "12px", marginTop: "5px", fontWeight: 600 }}>
                  {pricing.currency} / {billingLabel}
                </div>
              </>
            ) : tier === "trial" ? (
              <div style={{ fontSize: "15px", fontWeight: 700, color: cfg.accentColor, fontFamily: FONT, lineHeight: 1.4, paddingRight: "20px" }}>
                Descubre las herramientas de la plataforma sin costo.
              </div>
            ) : (
              <div style={{ fontSize: "26px", fontWeight: 800, color: cfg.accentColor, fontFamily: FONT }}>
                Contáctanos
              </div>
            )}
          </div>

          <div style={{ height: "1px", background: cfg.dividerColor, marginBottom: "20px" }} />

          {/* Features */}
          <div style={{ flex: 1, marginBottom: "24px" }}>
            {featureList.length === 0 ? (
              <p style={{ color: cfg.subColor, fontSize: "13px", margin: 0 }}>Sin datos de características</p>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
                {featureList.map((f, i) => (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                    <span
                      style={{
                        width: "24px", height: "24px", borderRadius: "8px",
                        background: cfg.accentBg, border: `1px solid ${cfg.accentColor}25`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "12px", flexShrink: 0, marginTop: "1px",
                      }}
                    >
                      {f.icon}
                    </span>
                    <span style={{ fontSize: "13px", color: cfg.textColor, lineHeight: 1.55, fontWeight: 500 }}>
                      {f.text}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* CTA */}
          <button
            disabled={isDisabled}
            onClick={() => !isDisabled && onSelect && onSelect(plan)}
            style={{
              width: "100%", padding: "13px 0", borderRadius: "12px", border: "none",
              cursor: isDisabled ? "default" : "pointer",
              background: isDisabled
                ? cfg.isDark ? "rgba(255,255,255,0.08)" : "#f1f5f9"
                : cfg.buttonBg,
              color: isDisabled
                ? cfg.isDark ? "rgba(255,255,255,0.35)" : "#94a3b8"
                : "white",
              fontSize: "14px", fontWeight: 800, fontFamily: FONT,
              letterSpacing: "0.05em", transition: "all 0.2s ease",
              boxShadow: isDisabled ? "none" : cfg.buttonShadow,
            }}
            onMouseEnter={(e) => {
              if (!isDisabled) {
                e.currentTarget.style.transform = "scale(1.02)";
                e.currentTarget.style.filter = "brightness(1.08)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.filter = "brightness(1)";
            }}
          >
            {isCurrentPlan
              ? "✓  Plan Activo"
              : isCancelLocked
              ? "Cancelación activa"
              : isTrialPlan
              ? "Plan de Inicio"
              : `Obtener ${cfg.name}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Skeleton loader ──────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <>
      <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
      <div style={{ borderRadius: "20px", border: "2px solid #e2e8f0", background: "#fff", overflow: "hidden" }}>
        <div style={{ height: "4px", background: "#e2e8f0" }} />
        <div style={{ padding: "28px 26px", display: "flex", flexDirection: "column", gap: "18px" }}>
          {[44, 64, 18, 140, 44].map((h, i) => (
            <div
              key={i}
              style={{
                height: `${h}px`, borderRadius: "10px",
                background: "linear-gradient(90deg, #f0f0f0 25%, #f8f8f8 50%, #f0f0f0 75%)",
                backgroundSize: "200% 100%", animation: "shimmer 1.4s ease infinite",
              }}
            />
          ))}
        </div>
      </div>
    </>
  );
}

// ─── PendingPaymentBanner — Wompi async payment in progress ──────────────────
function PendingPaymentBanner({ onActivated }) {
  const [attempts, setAttempts] = useState(0);
  const resolvedRef = useRef(false);

  useEffect(() => {
    resolvedRef.current = false;
    waitForActivation({
      onTick: () => { if (!resolvedRef.current) setAttempts((a) => a + 1); },
      maxAttempts: 20,
      intervalMs: 5000,
    })
      .then(() => {
        resolvedRef.current = true;
        if (typeof onActivated === "function") onActivated();
      })
      .catch(() => {
        // Timeout reached — user stays on PendingPayment, may refresh manually
      });
    return () => { resolvedRef.current = true; };
  }, [onActivated]);

  return (
    <>
      <style>{`
        @keyframes ppb-spin  { to { transform: rotate(360deg); } }
        @keyframes ppb-pulse { 0%,100%{opacity:1} 50%{opacity:0.55} }
      `}</style>
      <div
        style={{
          maxWidth: "760px", margin: "0 auto 36px",
          borderRadius: "20px",
          border: "2px solid #fde68a",
          background: "#fffbeb",
          boxShadow: "0 4px 24px rgba(245,158,11,0.12)",
          padding: "22px 26px",
          display: "flex", alignItems: "center", gap: "20px",
          fontFamily: FONT,
        }}
      >
        {/* Spinner */}
        <div
          style={{
            width: "44px", height: "44px", flexShrink: 0,
            border: "3px solid #fde68a",
            borderTopColor: "#F59E0B",
            borderRadius: "50%",
            animation: "ppb-spin 0.9s linear infinite",
          }}
        />
        <div style={{ flex: 1 }}>
          <p style={{ margin: "0 0 5px", fontSize: "15px", fontWeight: 800, color: "#92400e" }}>
            ⏳ Procesando pago…
          </p>
          <p
            style={{
              margin: 0, fontSize: "13px", color: "#a16207",
              lineHeight: 1.65, animation: "ppb-pulse 2.2s ease infinite",
            }}
          >
            Estamos verificando tu pago con Wompi. Esto puede tomar unos momentos.
            {attempts > 0 && (
              <span style={{ fontSize: "11px", opacity: 0.7, marginLeft: "6px" }}>
                (verificación {attempts}/20)
              </span>
            )}
          </p>
        </div>
      </div>
    </>
  );
}

// ─── CanceledBanner — subscription canceled, shows end date + re-subscribe CTA ─
function CanceledBanner({ subscription, onResubscribe }) {
  const endDate = formatUTCDate(subscription?.next_billing_date);

  return (
    <div
      style={{
        maxWidth: "760px", margin: "0 auto 36px",
        borderRadius: "20px",
        border: "2px solid rgba(247,46,65,0.3)",
        background: "#fff5f5",
        boxShadow: "0 4px 24px rgba(247,46,65,0.08)",
        padding: "22px 26px",
        display: "flex", alignItems: "flex-start", gap: "16px",
        fontFamily: FONT,
      }}
    >
      <span style={{ fontSize: "28px", flexShrink: 0, marginTop: "2px" }}>🚫</span>
      <div style={{ flex: 1 }}>
        <p style={{ margin: "0 0 5px", fontSize: "15px", fontWeight: 800, color: RED_DK }}>
          Suscripción cancelada
        </p>
        <p style={{ margin: "0 0 16px", fontSize: "13px", color: RED_DK, opacity: 0.85, lineHeight: 1.65 }}>
          {endDate ? (
            <>
              Tu acceso al plan está activo hasta el <strong>{endDate}</strong>.
              Después de esa fecha perderás el acceso a las funciones premium.
            </>
          ) : (
            "Tu suscripción ha sido cancelada. Elige un plan para continuar."
          )}
        </p>
        <button
          onClick={onResubscribe}
          style={{
            background: `linear-gradient(135deg, ${RED}, ${RED_DK})`,
            color: "#ffffff", border: "none", borderRadius: "10px",
            padding: "10px 22px", fontSize: "13px", fontWeight: 800,
            cursor: "pointer", fontFamily: FONT,
            boxShadow: "0 4px 14px rgba(247,46,65,0.35)",
            letterSpacing: "0.04em", transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.filter = "brightness(1.08)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.filter = "brightness(1)"; }}
        >
          🔄 Re-suscribirme
        </button>
      </div>
    </div>
  );
}

// ─── LastExpiredPanel — shown in 'none' state when last_expired exists ────────
function LastExpiredPanel({ lastExpired }) {
  if (!lastExpired) return null;

  const isSuspended = lastExpired.status === "Suspended";
  const tierCfg     = TIER_CONFIG[lastExpired.plan_tier?.toLowerCase()] || TIER_CONFIG.basic;
  const endDate     = formatUTCDate(lastExpired.period_end);

  return (
    <div
      style={{
        maxWidth: "760px", margin: "0 auto 32px",
        borderRadius: "16px",
        border: `1.5px solid ${isSuspended ? "#fde68a" : "rgba(247,46,65,0.22)"}`,
        background: isSuspended ? "#fffbeb" : "#fff5f5",
        padding: "18px 22px",
        display: "flex", alignItems: "flex-start", gap: "14px",
        fontFamily: FONT,
      }}
    >
      <span style={{ fontSize: "22px", flexShrink: 0, marginTop: "2px" }}>
        {isSuspended ? "⚠️" : "📋"}
      </span>
      <div>
        <p
          style={{
            margin: "0 0 4px", fontSize: "14px", fontWeight: 800,
            color: isSuspended ? "#92400e" : RED_DK,
          }}
        >
          {isSuspended ? "Plan suspendido" : "Suscripción cancelada anteriormente"}
        </p>
        <p
          style={{
            margin: 0, fontSize: "13px",
            color: isSuspended ? "#a16207" : RED_DK,
            opacity: 0.88, lineHeight: 1.6,
          }}
        >
          {isSuspended ? (
            <>
              Tu plan <strong>{tierCfg.emoji} {tierCfg.name}</strong> fue suspendido
              {endDate ? <> el <strong>{endDate}</strong></> : ""}.
              {" "}Para continuar usando todas las funciones, elige un plan a continuación.
            </>
          ) : (
            <>
              Cancelaste tu plan <strong>{tierCfg.emoji} {tierCfg.name}</strong>
              {endDate ? <> el <strong>{endDate}</strong></> : ""}.
              {" "}¡Vuelve cuando quieras! Elige un plan para retomar tus beneficios.
            </>
          )}
          {lastExpired.amount != null && (
            <span style={{ marginLeft: "4px", opacity: 0.7 }}>
              ({formatPrice(lastExpired.amount, lastExpired.currency)}
              /{BILLING_LABEL[lastExpired.billing_type] || "mes"})
            </span>
          )}
        </p>
      </div>
    </div>
  );
}

// ─── Cancellation Modal (retention-focused) ──────────────────────────────────
function CancellationModal({ isOpen, onClose, onConfirm, currentTier, isLoading }) {
  if (!isOpen) return null;
  const tierName = TIER_CONFIG[currentTier?.toLowerCase()]?.name || currentTier || "actual";
  // Pull 2 key benefits from currentTier features to show what they'll lose
  const keyBenefits = [
    { icon: "📊", text: "Análisis avanzados de tu restaurante" },
    { icon: "✨", text: "Mejora de imágenes con inteligencia artificial" },
    { icon: "🚀", text: "Pedidos a domicilio por WhatsApp" },
  ];

  return (
    <>
      <style>{`
        @keyframes cancel-modal-in {
          0%   { opacity:0; transform:scale(0.94) translateY(10px); }
          100% { opacity:1; transform:scale(1) translateY(0); }
        }
        @keyframes cancel-overlay-in { 0%{opacity:0} 100%{opacity:1} }
      `}</style>

      {/* Overlay */}
      <div
        onClick={!isLoading ? onClose : undefined}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(7,7,36,0.55)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          zIndex: 10000,
          animation: "cancel-overlay-in 0.2s ease both",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "24px",
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#ffffff",
            borderRadius: "24px",
            width: "100%",
            maxWidth: "460px",
            overflow: "hidden",
            boxShadow: "0 32px 80px rgba(0,0,0,0.22)",
            animation: "cancel-modal-in 0.35s cubic-bezier(.34,1.56,.64,1) both",
            fontFamily: FONT,
          }}
        >
          {/* Header */}
          <div
            style={{
              background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
              padding: "28px 28px 22px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: "56px", height: "56px",
                borderRadius: "16px",
                background: "rgba(255,255,255,0.08)",
                border: "1.5px solid rgba(255,255,255,0.14)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "26px",
                margin: "0 auto 14px",
              }}
            >
              😕
            </div>
            <h2
              style={{
                color: "#ffffff", fontSize: "19px", fontWeight: 800,
                margin: "0 0 6px", letterSpacing: "-0.3px",
              }}
            >
              ¿Seguro que deseas cancelar?
            </h2>
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "13px", margin: 0 }}>
              Plan <strong style={{ color: "rgba(255,255,255,0.9)" }}>{tierName}</strong>
            </p>
          </div>

          {/* Body */}
          <div style={{ padding: "24px 28px 28px" }}>
            <p
              style={{
                color: "#334155", fontSize: "14px", lineHeight: 1.7,
                margin: "0 0 20px", textAlign: "center",
              }}
            >
              Mantendrás acceso a todos tus beneficios hasta el final de tu ciclo de
              facturación actual. Después perderás acceso a:
            </p>

            {/* Benefits list (what they lose) */}
            <div
              style={{
                background: "#fff5f5",
                border: "1px solid rgba(247,46,65,0.18)",
                borderRadius: "14px",
                padding: "14px 16px",
                marginBottom: "22px",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              {keyBenefits.map((b, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span
                    style={{
                      width: "28px", height: "28px",
                      borderRadius: "8px",
                      background: "rgba(247,46,65,0.08)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "14px", flexShrink: 0,
                      textDecoration: "line-through",
                    }}
                  >
                    {b.icon}
                  </span>
                  <span
                    style={{
                      fontSize: "13px", color: "#94a3b8",
                      textDecoration: "line-through", lineHeight: 1.4,
                    }}
                  >
                    {b.text}
                  </span>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {/* Primary — keep plan */}
              <button
                onClick={onClose}
                disabled={isLoading}
                style={{
                  background: `linear-gradient(135deg, ${RED}, ${RED_DK})`,
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "12px",
                  padding: "13px 24px",
                  fontSize: "14px", fontWeight: 800, fontFamily: FONT,
                  cursor: isLoading ? "not-allowed" : "pointer",
                  boxShadow: "0 4px 16px rgba(247,46,65,0.35)",
                  letterSpacing: "0.04em",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.filter = "brightness(1.08)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.filter = "brightness(1)"; }}
              >
                💪 Mantener mi plan {tierName}
              </button>

              {/* Secondary — confirm cancel */}
              <button
                onClick={onConfirm}
                disabled={isLoading}
                style={{
                  background: "transparent",
                  color: isLoading ? "#cbd5e1" : "#94a3b8",
                  border: "2px solid #e2e8f0",
                  borderRadius: "12px",
                  padding: "11px 24px",
                  fontSize: "13px", fontWeight: 700, fontFamily: FONT,
                  cursor: isLoading ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.borderColor = RED;
                    e.currentTarget.style.color = RED;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e2e8f0";
                  e.currentTarget.style.color = isLoading ? "#cbd5e1" : "#94a3b8";
                }}
              >
                {isLoading ? (
                  <>
                    <span
                      style={{
                        width: "14px", height: "14px",
                        border: "2px solid #cbd5e1",
                        borderTopColor: "transparent",
                        borderRadius: "50%",
                        display: "inline-block",
                        animation: "spin 0.7s linear infinite",
                      }}
                    />
                    Procesando...
                  </>
                ) : (
                  "Sí, cancelar suscripción"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}

// ─── Payment Method Modal ──────────────────────────────────────────────────────
function PaymentMethodModal({ isOpen, onClose, onSelect, isLoading }) {
  if (!isOpen) return null;

  return (
    <>
      <style>{`
        .pm-btn {
          background: #ffffff;
          border: 2px solid #e2e8f0;
          border-radius: 16px;
          padding: 20px;
          text-align: left;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .pm-btn:hover:not(:disabled) {
          border-color: ${RED};
          background: #fffafa;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(247,46,65,0.12);
        }
        .pm-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
      <div
        onClick={!isLoading ? onClose : undefined}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(7,7,36,0.55)",
          backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
          zIndex: 10000,
          animation: "overlay-in 0.2s ease both",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "24px", fontFamily: FONT,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#ffffff", borderRadius: "24px",
            width: "100%", maxWidth: "440px", overflow: "hidden",
            boxShadow: "0 32px 80px rgba(0,0,0,0.22)",
            animation: "modal-in 0.35s cubic-bezier(.34,1.56,.64,1) both",
          }}
        >
          <div style={{
            background: "linear-gradient(135deg, #0f172a, #1e293b)",
            padding: "28px 28px 20px", textAlign: "center",
          }}>
            <h2 style={{ color: "#ffffff", fontSize: "20px", fontWeight: 800, margin: 0 }}>
              ¿Cómo deseas pagar?
            </h2>
          </div>
          <div style={{ padding: "24px 28px 28px", display: "flex", flexDirection: "column", gap: "16px" }}>
            <button className="pm-btn" onClick={() => onSelect("CARD")} disabled={isLoading}>
              <div style={{ fontSize: "32px" }}>💳</div>
              <div>
                <div style={{ fontSize: "16px", fontWeight: 800, color: "#0f172a", marginBottom: "4px" }}>
                  Tarjeta de Crédito/Débito
                </div>
                <div style={{ fontSize: "13px", color: "#64748b", lineHeight: 1.4 }}>
                  Recomendado. Suscripción automática sin interrupciones.
                </div>
              </div>
            </button>
            <button className="pm-btn" onClick={() => onSelect("PSE")} disabled={isLoading}>
              <div style={{ fontSize: "32px" }}>🏦</div>
              <div>
                <div style={{ fontSize: "16px", fontWeight: 800, color: "#0f172a", marginBottom: "4px" }}>
                  PSE / Bancolombia / Nequi
                </div>
                <div style={{ fontSize: "13px", color: "#64748b", lineHeight: 1.4 }}>
                  Pago único manual. Tendrás que renovar tu plan cada ciclo.
                </div>
              </div>
            </button>
            <button
              onClick={onClose}
              disabled={isLoading}
              style={{
                marginTop: "8px", background: "transparent", color: "#64748b",
                border: "none", fontSize: "14px", fontWeight: 700, cursor: "pointer",
                padding: "12px", borderRadius: "12px",
              }}
              onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.background = "#f1f5f9"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Wompi Processing Overlay ─────────────────────────────────────────────────
// Full-screen overlay shown during tokenization → polling → success states.
// Prevents user interaction while the async payment flow is in progress.
function WompiProcessingOverlay({ widgetState, errorMsg, onRetry, onDismissError }) {
  const isVisible = widgetState === WIDGET_STATE.TOKENIZING
                 || widgetState === WIDGET_STATE.POLLING
                 || widgetState === WIDGET_STATE.SUCCESS
                 || widgetState === WIDGET_STATE.ERROR;

  if (!isVisible) return null;

  const isError   = widgetState === WIDGET_STATE.ERROR;
  const isSuccess = widgetState === WIDGET_STATE.SUCCESS;
  const isPolling = widgetState === WIDGET_STATE.POLLING;

  const label = isError   ? null
              : isSuccess ? "¡Suscripción activada! Redirigiendo…"
              : isPolling ? "Verificando activación de tu suscripción…"
              :             "Registrando método de pago…";

  return (
    <>
      <style>{`
        @keyframes wpo-spin    { to { transform: rotate(360deg); } }
        @keyframes wpo-fadein  { 0%{opacity:0} 100%{opacity:1} }
        @keyframes wpo-scalein { 0%{opacity:0;transform:scale(0.9)} 100%{opacity:1;transform:scale(1)} }
        @keyframes wpo-pulse   { 0%,100%{opacity:1} 50%{opacity:0.55} }
      `}</style>

      {/* Backdrop */}
      <div style={{
        position: "fixed", inset: 0,
        background: "rgba(7,7,36,0.72)",
        backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
        zIndex: 20000,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "24px",
        animation: "wpo-fadein 0.25s ease both",
        fontFamily: FONT,
      }}>
        <div style={{
          background: "#ffffff",
          borderRadius: "28px",
          width: "100%", maxWidth: "400px",
          padding: "44px 36px",
          boxShadow: "0 32px 80px rgba(0,0,0,0.28)",
          animation: "wpo-scalein 0.35s cubic-bezier(.34,1.56,.64,1) both",
          textAlign: "center",
          display: "flex", flexDirection: "column", alignItems: "center", gap: "20px",
        }}>

          {/* Icon / spinner */}
          {isError ? (
            <div style={{
              width: "64px", height: "64px", borderRadius: "20px",
              background: "#fff5f5", border: "2px solid rgba(247,46,65,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "30px",
            }}>❌</div>
          ) : isSuccess ? (
            <div style={{
              width: "64px", height: "64px", borderRadius: "20px",
              background: "#f0fdf4", border: "2px solid #bbf7d0",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "30px",
            }}>✅</div>
          ) : (
            <div style={{
              width: "56px", height: "56px", flexShrink: 0,
              border: "4px solid #e2e8f0",
              borderTopColor: RED,
              borderRadius: "50%",
              animation: "wpo-spin 0.85s linear infinite",
            }} />
          )}

          {/* Title */}
          <div>
            <p style={{ margin: "0 0 6px", fontSize: "17px", fontWeight: 800, color: isError ? RED_DK : "#0f172a" }}>
              {isError   ? "Ocurrió un problema"
               : isSuccess ? "¡Listo!"
               : "Procesando pago"}
            </p>
            {label && (
              <p style={{
                margin: 0, fontSize: "13px", color: "#64748b", lineHeight: 1.65,
                animation: (!isError && !isSuccess) ? "wpo-pulse 2s ease infinite" : "none",
              }}>
                {label}
              </p>
            )}
            {isError && errorMsg && (
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: RED_DK, lineHeight: 1.6 }}>
                {errorMsg}
              </p>
            )}
          </div>

          {/* Wompi branding */}
          {!isError && !isSuccess && (
            <div style={{
              display: "flex", alignItems: "center", gap: "6px",
              background: "#f8fafc", border: "1px solid #e2e8f0",
              borderRadius: "999px", padding: "5px 14px",
            }}>
              <span style={{ fontSize: "12px" }}>🔒</span>
              <span style={{ fontSize: "11px", color: "#64748b", fontWeight: 600 }}>
                Pago seguro con Wompi
              </span>
            </div>
          )}

          {/* Error actions */}
          {isError && (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}>
              <button
                onClick={onRetry}
                style={{
                  background: `linear-gradient(135deg, ${RED}, ${RED_DK})`,
                  color: "#fff", border: "none", borderRadius: "12px",
                  padding: "12px 24px", fontSize: "14px", fontWeight: 800,
                  cursor: "pointer", fontFamily: FONT,
                  boxShadow: "0 4px 14px rgba(247,46,65,0.35)",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.filter = "brightness(1.08)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.filter = "brightness(1)"; }}
              >
                🔄 Intentar de nuevo
              </button>
              <button
                onClick={onDismissError}
                style={{
                  background: "transparent", color: "#64748b",
                  border: "2px solid #e2e8f0", borderRadius: "12px",
                  padding: "10px 24px", fontSize: "13px", fontWeight: 700,
                  cursor: "pointer", fontFamily: FONT, transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                Cancelar
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Upgrade Modal ─────────────────────────────────────────────────────────────
// Shown when user selects a plan with a higher tier_rank than the current plan.
// Charge is immediate and cycle resets.
function UpgradeModal({ isOpen, onClose, onConfirm, plan, isLoading }) {
  if (!isOpen || !plan) return null;
  const cfg = TIER_CONFIG[plan.tier] || TIER_CONFIG.basic;

  return (
    <>
      <style>{`
        @keyframes um-in { 0%{opacity:0;transform:scale(0.92) translateY(14px)} 100%{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes um-overlay { 0%{opacity:0} 100%{opacity:1} }
        @keyframes um-spin { to { transform: rotate(360deg); } }
      `}</style>
      <div
        onClick={!isLoading ? onClose : undefined}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(7,7,36,0.58)",
          backdropFilter: "blur(5px)", WebkitBackdropFilter: "blur(5px)",
          zIndex: 10500,
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "24px",
          animation: "um-overlay 0.22s ease both",
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#ffffff", borderRadius: "26px",
            width: "100%", maxWidth: "440px",
            overflow: "hidden",
            boxShadow: "0 28px 72px rgba(0,0,0,0.24)",
            animation: "um-in 0.32s cubic-bezier(.34,1.56,.64,1) both",
            fontFamily: FONT,
          }}
        >
          {/* Header */}
          <div style={{
            background: `linear-gradient(135deg, ${RED} 0%, ${RED_DK} 100%)`,
            padding: "28px 28px 22px", textAlign: "center",
          }}>
            <div style={{
              width: "52px", height: "52px", borderRadius: "16px",
              background: "rgba(255,255,255,0.15)",
              border: "1.5px solid rgba(255,255,255,0.3)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "24px", margin: "0 auto 12px",
            }}>⚡</div>
            <h2 style={{ color: "#fff", fontSize: "18px", fontWeight: 900, margin: 0, letterSpacing: "-0.3px" }}>
              Confirmar Upgrade
            </h2>
            <p style={{ color: "rgba(255,255,255,0.75)", fontSize: "12px", margin: "4px 0 0", fontWeight: 600 }}>
              Cobro inmediato · Ciclo reiniciado
            </p>
          </div>

          {/* Body */}
          <div style={{ padding: "24px 28px 28px" }}>
            {/* Plan chip */}
            <div style={{
              display: "flex", alignItems: "center", gap: "12px",
              background: "#f8fafc", border: `1.5px solid ${cfg.accentColor}25`,
              borderRadius: "14px", padding: "12px 16px", marginBottom: "20px",
            }}>
              <div style={{
                width: "38px", height: "38px", borderRadius: "10px",
                background: `${cfg.accentColor}15`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "18px", flexShrink: 0,
              }}>{cfg.emoji}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "15px", fontWeight: 800, color: "#0f172a" }}>Plan {cfg.name}</div>
                {plan.pricing && (
                  <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                    {formatPrice(plan.pricing.amount, plan.pricing.currency)} /{" "}
                    {BILLING_LABEL[plan.pricing.billing_interval] || "mes"}
                  </div>
                )}
              </div>
              <div style={{
                background: `${cfg.accentColor}15`, color: cfg.accentColor,
                fontSize: "10px", fontWeight: 800, padding: "4px 10px",
                borderRadius: "999px", letterSpacing: "0.06em", textTransform: "uppercase",
              }}>{plan.tier}</div>
            </div>

            {/* Charge notice */}
            <div style={{
              background: "#fff5f5", border: "1.5px solid rgba(247,46,65,0.2)",
              borderRadius: "12px", padding: "14px 16px", marginBottom: "22px",
              display: "flex", gap: "10px", alignItems: "flex-start",
            }}>
              <span style={{ fontSize: "16px", flexShrink: 0 }}>💳</span>
              <p style={{ margin: 0, fontSize: "13px", color: RED_DK, lineHeight: 1.6, fontWeight: 600 }}>
                Se te cobrará{" "}
                <strong>{plan.pricing ? formatPrice(plan.pricing.amount, plan.pricing.currency) : "—"}</strong>{" "}
                hoy y tu ciclo de facturación se reiniciará.
              </p>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <button
                onClick={onConfirm}
                disabled={isLoading}
                style={{
                  background: isLoading ? "#e2e8f0" : `linear-gradient(135deg, ${RED}, ${RED_DK})`,
                  color: isLoading ? "#94a3b8" : "#fff",
                  border: "none", borderRadius: "13px",
                  padding: "14px 24px", fontSize: "14px", fontWeight: 800,
                  cursor: isLoading ? "not-allowed" : "pointer", fontFamily: FONT,
                  boxShadow: isLoading ? "none" : "0 4px 18px rgba(247,46,65,0.4)",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.filter = "brightness(1.08)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.filter = "brightness(1)"; }}
              >
                {isLoading ? (
                  <>
                    <span style={{
                      width: "15px", height: "15px", border: "2px solid #94a3b8",
                      borderTopColor: "transparent", borderRadius: "50%",
                      display: "inline-block", animation: "um-spin 0.7s linear infinite",
                    }} />
                    Procesando…
                  </>
                ) : "⚡ Confirmar y pagar ahora"}
              </button>
              <button
                onClick={onClose}
                disabled={isLoading}
                style={{
                  background: "#f8fafc", color: "#475569",
                  border: "2px solid #e2e8f0", borderRadius: "13px",
                  padding: "12px 24px", fontSize: "14px", fontWeight: 700,
                  cursor: isLoading ? "not-allowed" : "pointer", fontFamily: FONT,
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.borderColor = "#cbd5e1"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Downgrade Modal ───────────────────────────────────────────────────────────
// Shown when user selects a plan with a lower tier_rank than the current plan.
// Change is deferred to the end of the current billing period.
function DowngradeModal({ isOpen, onClose, onConfirm, plan, nextBillingDate, isLoading }) {
  if (!isOpen || !plan) return null;
  const cfg = TIER_CONFIG[plan.tier] || TIER_CONFIG.basic;

  return (
    <>
      <style>{`
        @keyframes dm-in { 0%{opacity:0;transform:scale(0.92) translateY(14px)} 100%{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes dm-overlay { 0%{opacity:0} 100%{opacity:1} }
        @keyframes dm-spin { to { transform: rotate(360deg); } }
      `}</style>
      <div
        onClick={!isLoading ? onClose : undefined}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(7,7,36,0.55)",
          backdropFilter: "blur(5px)", WebkitBackdropFilter: "blur(5px)",
          zIndex: 10500,
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "24px",
          animation: "dm-overlay 0.22s ease both",
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#ffffff", borderRadius: "26px",
            width: "100%", maxWidth: "460px",
            overflow: "hidden",
            boxShadow: "0 28px 72px rgba(0,0,0,0.22)",
            animation: "dm-in 0.32s cubic-bezier(.34,1.56,.64,1) both",
            fontFamily: FONT,
          }}
        >
          {/* Header */}
          <div style={{
            background: "linear-gradient(135deg, #334155 0%, #1e293b 100%)",
            padding: "28px 28px 22px", textAlign: "center",
          }}>
            <div style={{
              width: "52px", height: "52px", borderRadius: "16px",
              background: "rgba(255,255,255,0.12)",
              border: "1.5px solid rgba(255,255,255,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "24px", margin: "0 auto 12px",
            }}>📅</div>
            <h2 style={{ color: "#fff", fontSize: "18px", fontWeight: 900, margin: 0, letterSpacing: "-0.3px" }}>
              Cambio de plan programado
            </h2>
            <p style={{ color: "rgba(255,255,255,0.65)", fontSize: "12px", margin: "4px 0 0", fontWeight: 600 }}>
              Sin cobro hoy · Efectivo al próximo ciclo
            </p>
          </div>

          {/* Body */}
          <div style={{ padding: "24px 28px 28px" }}>
            {/* Target plan chip */}
            <div style={{
              display: "flex", alignItems: "center", gap: "12px",
              background: "#f8fafc", border: "1.5px solid #e2e8f0",
              borderRadius: "14px", padding: "12px 16px", marginBottom: "18px",
            }}>
              <div style={{
                width: "38px", height: "38px", borderRadius: "10px",
                background: `${cfg.accentColor}15`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "18px", flexShrink: 0,
              }}>{cfg.emoji}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "15px", fontWeight: 800, color: "#0f172a" }}>Plan {cfg.name}</div>
                {plan.pricing && (
                  <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                    {formatPrice(plan.pricing.amount, plan.pricing.currency)} /{" "}
                    {BILLING_LABEL[plan.pricing.billing_interval] || "mes"}
                  </div>
                )}
              </div>
            </div>

            {/* Deferral notice */}
            <div style={{
              background: "#f0f9ff", border: "1.5px solid #bae6fd",
              borderRadius: "12px", padding: "14px 16px", marginBottom: "14px",
              display: "flex", gap: "10px", alignItems: "flex-start",
            }}>
              <span style={{ fontSize: "16px", flexShrink: 0 }}>📋</span>
              <p style={{ margin: 0, fontSize: "13px", color: "#0c4a6e", lineHeight: 1.65 }}>
                Mantendrás tu plan actual hasta{" "}
                <strong>{nextBillingDate || "el final de tu período"}</strong>.{" "}
                No se cobrará nada hoy. El cambio será efectivo al inicio de tu próximo período.
              </p>
            </div>

            {/* Feature loss warning */}
            <div style={{
              background: "#fffbeb", border: "1.5px solid #fde68a",
              borderRadius: "12px", padding: "12px 16px", marginBottom: "22px",
              display: "flex", gap: "10px", alignItems: "flex-start",
            }}>
              <span style={{ fontSize: "14px", flexShrink: 0 }}>⚠️</span>
              <p style={{ margin: 0, fontSize: "12px", color: "#92400e", lineHeight: 1.55 }}>
                Al cambiar a un plan inferior perderás acceso a algunas características premium cuando el cambio sea efectivo.
              </p>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <button
                onClick={onConfirm}
                disabled={isLoading}
                style={{
                  background: isLoading ? "#e2e8f0" : "linear-gradient(135deg, #334155, #1e293b)",
                  color: isLoading ? "#94a3b8" : "#fff",
                  border: "none", borderRadius: "13px",
                  padding: "14px 24px", fontSize: "14px", fontWeight: 800,
                  cursor: isLoading ? "not-allowed" : "pointer", fontFamily: FONT,
                  boxShadow: isLoading ? "none" : "0 4px 14px rgba(0,0,0,0.2)",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.filter = "brightness(1.12)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.filter = "brightness(1)"; }}
              >
                {isLoading ? (
                  <>
                    <span style={{
                      width: "15px", height: "15px", border: "2px solid #94a3b8",
                      borderTopColor: "transparent", borderRadius: "50%",
                      display: "inline-block", animation: "dm-spin 0.7s linear infinite",
                    }} />
                    Procesando…
                  </>
                ) : "📅 Confirmar cambio"}
              </button>
              <button
                onClick={onClose}
                disabled={isLoading}
                style={{
                  background: "#f8fafc", color: "#475569",
                  border: "2px solid #e2e8f0", borderRadius: "13px",
                  padding: "12px 24px", fontSize: "14px", fontWeight: 700,
                  cursor: isLoading ? "not-allowed" : "pointer", fontFamily: FONT,
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.borderColor = "#cbd5e1"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Action Overlay ────────────────────────────────────────────────────────────
// Full-screen overlay for upgrade polling / success / error states.
// Separate from WompiProcessingOverlay (which is Wompi widget specific).
function ActionOverlay({ state, errorMsg, onRetry, onDismiss }) {
  // state: null | "polling" | "success" | "error"
  if (!state) return null;

  const isError   = state === "error";
  const isSuccess = state === "success";

  const label = isError   ? null
              : isSuccess ? "¡Upgrade completado! Actualizando tu plan…"
              :             "Verificando activación de tu suscripción…";

  return (
    <>
      <style>{`
        @keyframes ao-spin    { to { transform: rotate(360deg); } }
        @keyframes ao-fadein  { 0%{opacity:0} 100%{opacity:1} }
        @keyframes ao-scalein { 0%{opacity:0;transform:scale(0.9)} 100%{opacity:1;transform:scale(1)} }
        @keyframes ao-pulse   { 0%,100%{opacity:1} 50%{opacity:0.5} }
      `}</style>
      <div style={{
        position: "fixed", inset: 0,
        background: "rgba(7,7,36,0.70)",
        backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
        zIndex: 20000,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "24px",
        animation: "ao-fadein 0.25s ease both",
        fontFamily: FONT,
      }}>
        <div style={{
          background: "#ffffff", borderRadius: "28px",
          width: "100%", maxWidth: "400px",
          padding: "44px 36px",
          boxShadow: "0 32px 80px rgba(0,0,0,0.28)",
          animation: "ao-scalein 0.35s cubic-bezier(.34,1.56,.64,1) both",
          textAlign: "center",
          display: "flex", flexDirection: "column", alignItems: "center", gap: "20px",
        }}>
          {/* Icon */}
          {isError ? (
            <div style={{
              width: "64px", height: "64px", borderRadius: "20px",
              background: "#fff5f5", border: "2px solid rgba(247,46,65,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: "30px",
            }}>❌</div>
          ) : isSuccess ? (
            <div style={{
              width: "64px", height: "64px", borderRadius: "20px",
              background: "#f0fdf4", border: "2px solid #bbf7d0",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: "30px",
            }}>✅</div>
          ) : (
            <div style={{
              width: "56px", height: "56px",
              border: "4px solid #e2e8f0", borderTopColor: RED,
              borderRadius: "50%", animation: "ao-spin 0.85s linear infinite",
            }} />
          )}

          {/* Text */}
          <div>
            <p style={{ margin: "0 0 6px", fontSize: "17px", fontWeight: 800, color: isError ? RED_DK : "#0f172a" }}>
              {isError ? "Ocurrió un problema" : isSuccess ? "¡Listo!" : "Procesando upgrade"}
            </p>
            {label && (
              <p style={{
                margin: 0, fontSize: "13px", color: "#64748b", lineHeight: 1.65,
                animation: (!isError && !isSuccess) ? "ao-pulse 2s ease infinite" : "none",
              }}>
                {label}
              </p>
            )}
            {isError && errorMsg && (
              <p style={{ margin: "6px 0 0", fontSize: "13px", color: RED_DK, lineHeight: 1.6 }}>
                {errorMsg}
              </p>
            )}
          </div>

          {/* Branding chip for polling */}
          {!isError && !isSuccess && (
            <div style={{
              display: "flex", alignItems: "center", gap: "6px",
              background: "#f8fafc", border: "1px solid #e2e8f0",
              borderRadius: "999px", padding: "5px 14px",
            }}>
              <span style={{ fontSize: "12px" }}>⚡</span>
              <span style={{ fontSize: "11px", color: "#64748b", fontWeight: 600 }}>Upgrade en progreso</span>
            </div>
          )}

          {/* Error actions */}
          {isError && (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}>
              <button
                onClick={onRetry}
                style={{
                  background: `linear-gradient(135deg, ${RED}, ${RED_DK})`,
                  color: "#fff", border: "none", borderRadius: "12px",
                  padding: "12px 24px", fontSize: "14px", fontWeight: 800,
                  cursor: "pointer", fontFamily: FONT,
                  boxShadow: "0 4px 14px rgba(247,46,65,0.35)",
                }}
              >🔄 Intentar de nuevo</button>
              <button
                onClick={onDismiss}
                style={{
                  background: "#f8fafc", color: "#64748b",
                  border: "2px solid #e2e8f0", borderRadius: "12px",
                  padding: "10px 24px", fontSize: "13px", fontWeight: 700,
                  cursor: "pointer", fontFamily: FONT,
                }}
              >Cancelar</button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}


// ─── Main Module ──────────────────────────────────────────────────────────────

export default function PlanesModule() {
  const navigate = useNavigate();
  const { plans, currentTier, isLoading, error, refetch, countryMissing, trustedPricingIds } =
    useSubscriptionPlans();
  const {
    subscription,
    isLoading: subLoading,
    refetch: refetchSub,
    markCancelled,
    markDowngradeScheduled,
  } = useActiveSubscription();

  // ── Master state derivation (Wompi) ───────────────────────────────────────
  const masterState = (() => {
    if (subLoading)                               return "loading";
    if (!subscription)                            return "none";
    if (subscription.status === "Active")         return "active";
    if (subscription.status === "PendingPayment") return "pending";
    if (subscription.status === "canceled")       return "canceled";
    return "active";
  })();

  const isPending      = masterState === "pending";
  const isTrialPlan    = currentTier?.toLowerCase() === "trial";
  // PendingPayment users renew via the same Widget flow (prepare-checkout detects the state automatically)
  const isFirstTime    = masterState === "none" || masterState === "canceled" || masterState === "pending" || isTrialPlan;

  // ── Numeric tier rank of the CURRENT subscription (from plan_features data)
  // Uses features.tier_rank — never compares plan name strings.
  const currentTierRank = useMemo(() => {
    if (!subscription?.plan_tier) return 0;
    const match = plans.find(
      (p) => p.tier === subscription.plan_tier.toLowerCase()
    );
    return match?.features?.tier_rank ?? 0;
  }, [plans, subscription]);

  // ── Cancellation already scheduled (from server state)?
  const cancellationScheduled     = subscription?.cancellation_scheduled === true;
  const cancellationDateFormatted = formatUTCDate(subscription?.cancellation_date);
  const nextBillingDateFormatted  = formatUTCDate(subscription?.next_billing_date);

  // ── Plan selection (first-time Wompi) ────────────────────────────────────
  const [selectedPlan,  setSelectedPlan]  = useState(null);
  const [modalOpen,     setModalOpen]     = useState(false);
  const [paymentMethodModalOpen, setPaymentMethodModalOpen] = useState(false);
  const [paymentMethodParams, setPaymentMethodParams] = useState(null);

  // ── Upgrade flow ──────────────────────────────────────────────────────────
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [isUpgrading,      setIsUpgrading]      = useState(false);
  const [overlayState,     setOverlayState]     = useState(null); // null|"polling"|"success"|"error"
  const [actionError,      setActionError]      = useState(null);

  // ── Downgrade flow ────────────────────────────────────────────────────────
  const [downgradeModalOpen, setDowngradeModalOpen] = useState(false);
  const [isDowngrading,      setIsDowngrading]      = useState(false);
  const [downgradeError,     setDowngradeError]     = useState(null);

  // ── Cancellation state ────────────────────────────────────────────────────
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [isCancelling,    setIsCancelling]    = useState(false);
  const [cancelError,     setCancelError]     = useState(null);

  // ── Wompi widget — v4.0 redirect-based flow ─────────────────────────────
  // has_payment_source drives upgrade routing (fetched dynamically in handleUpgrade)


  const { openWompiWidget, widgetState, errorMsg, reset: resetWidget } = useWompiWidget({
    onSuccess:           () => {
      refetch();
      refetchSub();
      setTimeout(() => {
        resetWidget();
        navigate("/");
      }, 1500);
    },
    onError:             (msg) => console.error("[Wompi]", msg),
    // error_code 0018 → user already has a subscription → go to dashboard
    onAlreadySubscribed: () => navigate("/"),
  });

  const isWompiProcessing = widgetState === WIDGET_STATE.PREPARING
                         || widgetState === WIDGET_STATE.LOADING
                         || widgetState === WIDGET_STATE.OPEN
                         || widgetState === WIDGET_STATE.CONFIRMING
                         || widgetState === WIDGET_STATE.POLLING;

  // Any operation in progress — used to guard modal close
  const isProcessing = isWompiProcessing || isUpgrading || isDowngrading;

  // ── Helpers ───────────────────────────────────────────────────────────────
  const token     = localStorage.getItem("token");
  const tokenType = localStorage.getItem("tokenType");

  function parseApiError(err) {
    const msg = typeof err === "string" ? err : err?.message || "";
    if (msg.includes("404")) return "No se encontró una suscripción activa.";
    if (msg.includes("400")) return "La solicitud no es válida. Inténtalo de nuevo.";
    return "Ocurrió un error inesperado. Por favor, contáctanos si el problema persiste.";
  }

  // ── Decision tree: handleSelectPlan ──────────────────────────────────────
  // Compares tier_rank numerically — NEVER plan name strings.
  const handleSelectPlan = useCallback((plan) => {
    const targetRank = plan.features?.tier_rank ?? 0;

    if (isFirstTime) {
      // No active subscription → open Wompi widget (new subscription)
      setSelectedPlan(plan);
      setModalOpen(true);
      return;
    }

    if (targetRank > currentTierRank) {
      // Upgrade: immediate charge + cycle reset
      setSelectedPlan(plan);
      setUpgradeModalOpen(true);
      return;
    }

    if (targetRank < currentTierRank) {
      // Downgrade: deferred to end of billing period
      setSelectedPlan(plan);
      setDowngradeModalOpen(true);
      return;
    }

    // targetRank === currentTierRank → same plan (button disabled in PlanCard, should not reach here)
  }, [isFirstTime, currentTierRank]);

  // ── First-time / re-subscribe confirmation (opens Wompi widget) ──────────
  const handleModalClose = useCallback(() => {
    if (isProcessing) return;
    setModalOpen(false);
    setSelectedPlan(null);
  }, [isProcessing]);

  const handleConfirm = useCallback(() => {
    if (!selectedPlan?.pricing?.subscription_pricing_id) {
      setActionError("Este plan no tiene un precio disponible para tu país. Contacta a soporte.");
      setModalOpen(false);
      return;
    }

    // ── SECURITY: reject any pricing_id not from the current country's price list ──
    if (!trustedPricingIds.has(selectedPlan.pricing.subscription_pricing_id)) {
      setActionError(
        "Plan no válido para tu país. Por favor recarga la página e intenta de nuevo."
      );
      setModalOpen(false);
      return;
    }

    // amountInCents, reference, and integrity_hash are fetched from
    // POST /prepare-checkout inside the hook — do NOT compute them here.
    setModalOpen(false);
    const rawLandingUrl = process.env.REACT_APP_LANDING_URL || "http://127.0.0.1:5500";
    const landingUrl = rawLandingUrl.replace(/\/+$/, "");
    window.open(`${landingUrl}/pago#action=start&plan_id=${selectedPlan.pricing.subscription_pricing_id}&upgrade_mode=false`, "_blank");
  }, [selectedPlan, trustedPricingIds]);

  // ── Upgrade handler ───────────────────────────────────────────────────────
  // POST /upgrade-subscription → poll GET /active-subscription every 3 s
  // Three outcomes: success (tier changed) | declined (card rejected) | timeout
  const handleUpgrade = useCallback(async () => {
    if (!selectedPlan?.pricing?.subscription_pricing_id) {
      setActionError("Este plan no tiene un precio configurado para tu país. No se puede procesar el upgrade.");
      setOverlayState("error");
      setUpgradeModalOpen(false);
      return;
    }

    // ── SECURITY: ensure pricing_id came from the backend for THIS country ──
    if (!trustedPricingIds.has(selectedPlan.pricing.subscription_pricing_id)) {
      setActionError(
        "Plan no válido para tu país. Por favor recarga la página e intenta de nuevo."
      );
      setOverlayState("error");
      setUpgradeModalOpen(false);
      return;
    }

    setIsUpgrading(true);
    setActionError(null);
    setUpgradeModalOpen(false);

    try {
      // Step 1: Fire the upgrade charge
      const response = await makeRequest(
        "POST",
        `subscriptions_router/upgrade-subscription?subscription_pricing_id=${selectedPlan.pricing.subscription_pricing_id}`,
        null, "application/json", token, tokenType, null, true
      );

      // New backend contract: response.status should be "pending"
      if (response?.status !== "pending") {
        throw new Error("El servidor no confirmó el inicio del upgrade.");
      }

      // Step 2: Poll for outcome (3 s × 10 = 30 s max)
      setOverlayState("polling");

      const { outcome } = await waitForUpgradeOutcome({
        expectedNewTier: selectedPlan.tier,
      });

      if (outcome === "success") {
        // Happy path — show celebration then refresh
        setOverlayState("success");
        setTimeout(() => {
          setOverlayState(null);
          setSelectedPlan(null);
          refetch();
          refetchSub();
        }, 1800);

      } else if (outcome === "declined") {
        // Card declined — redirect to landing (same behaviour as other payment failures)
        setOverlayState(null);
        const rawLandingUrl = process.env.REACT_APP_LANDING_URL || "http://127.0.0.1:5500";
        const landingUrl = rawLandingUrl.replace(/\/+$/, "");
        window.open(`${landingUrl}/pago#action=start&plan_id=${selectedPlan.pricing.subscription_pricing_id}&upgrade_mode=true`, "_blank");
        setSelectedPlan(null);

      } else {
        // Timeout — generic error, user can retry
        setActionError(
          "El upgrade está tardando más de lo esperado. Verifica el estado de tu suscripción."
        );
        setOverlayState("error");
      }

    } catch (err) {
      setActionError(parseApiError(err));
      setOverlayState("error");
    } finally {
      setIsUpgrading(false);
    }
  }, [selectedPlan, token, tokenType, refetch, refetchSub, trustedPricingIds]);


  // ── Downgrade handler ─────────────────────────────────────────────────────
  // POST /downgrade-subscription?subscription_pricing_id={id}  (no body)
  // Optimistically updates scheduled_plan locally on success.
  const handleDowngrade = useCallback(async () => {
    if (!selectedPlan?.pricing?.subscription_pricing_id) {
      setDowngradeError("Este plan no tiene un precio configurado para tu país. No se puede procesar el cambio.");
      setDowngradeModalOpen(false);
      return;
    }

    // ── SECURITY: ensure pricing_id came from the backend for THIS country ──
    if (!trustedPricingIds.has(selectedPlan.pricing.subscription_pricing_id)) {
      setDowngradeError(
        "Plan no válido para tu país. Por favor recarga la página e intenta de nuevo."
      );
      setDowngradeModalOpen(false);
      return;
    }

    setIsDowngrading(true);
    setDowngradeError(null);

    try {
      const response = await makeRequest(
        "POST",
        `subscriptions_router/downgrade-subscription?subscription_pricing_id=${selectedPlan.pricing.subscription_pricing_id}`,
        null, "application/json", token, tokenType, null, true
      );

      // Optimistic local update — avoids full refetch
      markDowngradeScheduled(
        response?.scheduled_plan || {
          plan_tier:      selectedPlan.tier,
          effective_date: subscription?.next_billing_date || null,
        }
      );

      setDowngradeModalOpen(false);
      setSelectedPlan(null);

    } catch (err) {
      setDowngradeError(parseApiError(err));
    } finally {
      setIsDowngrading(false);
    }
  }, [selectedPlan, token, tokenType, subscription, markDowngradeScheduled, trustedPricingIds]);

  // ── Cancel subscription handler ───────────────────────────────────────────
  const handleCancelSubscription = useCallback(async () => {
    setIsCancelling(true);
    setCancelError(null);
    try {
      const response = await makeRequest(
        "POST",
        "subscriptions_router/cancel-subscription",
        null, "application/json", token, tokenType, null, true
      );
      if (response?.canceled) {
        markCancelled(response.access_until || null);
        setCancelModalOpen(false);
      } else {
        setCancelError("No se pudo procesar la cancelación. Inténtalo de nuevo.");
      }
    } catch (err) {
      const msg = typeof err === "string" ? err : err?.message || "";
      if (msg.includes("404") || msg.toLowerCase().includes("no active")) {
        setCancelError("No encontramos una suscripción activa en tu cuenta.");
      } else if (msg.includes("400") || msg.toLowerCase().includes("already") || msg.toLowerCase().includes("programada")) {
        setCancelError("Tu suscripción ya está programada para cancelarse.");
        setCancelModalOpen(false);
      } else {
        setCancelError("Ocurrió un error al cancelar. Por favor, contáctanos si el problema persiste.");
      }
    } finally {
      setIsCancelling(false);
    }
  }, [markCancelled, token, tokenType]);

  // Scroll to plans grid (used by CanceledBanner CTA)
  const scrollToPlans = useCallback(() => {
    document.getElementById("plans-grid")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);


  return (
    <div
      style={{
        minHeight: "100%",
        background: "#ffffff",
        padding: "36px 24px 56px",
        boxSizing: "border-box",
        fontFamily: FONT,
      }}
    >
      {/* ── Country not configured — informational banner (non-blocking) ── */}
      {countryMissing && (
        <div
          style={{
            maxWidth: "760px", margin: "0 auto 36px",
            borderRadius: "18px",
            border: "1.5px solid #fde68a",
            background: "#fffbeb",
            boxShadow: "0 4px 20px rgba(245,158,11,0.1)",
            padding: "20px 24px",
            display: "flex", alignItems: "flex-start", gap: "14px",
            fontFamily: FONT,
          }}
        >
          <span style={{ fontSize: "26px", flexShrink: 0, marginTop: "2px" }}>🌍</span>
          <div>
            <p style={{ margin: "0 0 4px", fontSize: "14px", fontWeight: 800, color: "#92400e" }}>
              País de facturación no configurado
            </p>
            <p style={{ margin: 0, fontSize: "13px", color: "#a16207", lineHeight: 1.65 }}>
              Tu cuenta no tiene un país asociado. Los precios no están disponibles.
              Contacta a soporte para configurar tu región de facturación.
            </p>
          </div>
        </div>
      )}

      {/* ── Top-panel: state-driven banner area ────────────────────────── */}

      {/* PendingPayment — Wompi async processing */}
      {masterState === "pending" && (
        <PendingPaymentBanner
          onActivated={() => { refetch(); refetchSub(); }}
        />
      )}

      {/* Active — show current plan details */}
      {(masterState === "active" || masterState === "loading") && (
        <ActiveSubscriptionPanel subscription={subscription} isLoading={subLoading} />
      )}

      {/* Canceled — red banner with next_billing_date + re-subscribe CTA */}
      {masterState === "canceled" && (
        <CanceledBanner
          subscription={subscription}
          onResubscribe={scrollToPlans}
        />
      )}

      {/* None + last_expired — contextual suspension/cancellation message */}
      {masterState === "none" && subscription?.last_expired && (
        <LastExpiredPanel lastExpired={subscription.last_expired} />
      )}

      {/* ── Header ────────────────────────────────────────────────────── */}
      <div style={{ textAlign: "center", marginBottom: "52px" }}>
        <div
          style={{
            display: "inline-flex", alignItems: "center", gap: "8px",
            background: "rgba(247,46,65,0.07)", border: "1px solid rgba(247,46,65,0.2)",
            borderRadius: "999px", padding: "6px 18px", marginBottom: "18px",
          }}
        >
          <span style={{ fontSize: "14px" }}>💎</span>
          <span
            style={{
              color: RED, fontSize: "11px", fontWeight: 800,
              letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: FONT,
            }}
          >
            Planes de Suscripción
          </span>
        </div>

        <h1
          style={{
            fontSize: "clamp(28px, 5vw, 46px)", fontWeight: 900, color: "#0f172a",
            margin: "0 0 14px", fontFamily: FONT, lineHeight: 1.1, letterSpacing: "-1.5px",
          }}
        >
          Elige el plan perfecto<br />para tu{" "}
          <span
            style={{
              background: `linear-gradient(135deg, ${RED} 30%, #ff6b7a 100%)`,
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
            }}
          >
            restaurante
          </span>
        </h1>

        <p style={{ fontSize: "15px", color: "#64748b", margin: "0 auto", maxWidth: "480px", lineHeight: 1.65 }}>
          Herramientas diseñadas para restaurantes modernos.{" "}
          <strong style={{ color: "#1E1E1E" }}>Sin contratos</strong>, cancela cuando quieras.
        </p>

        {currentTier && (
          <div
            style={{
              marginTop: "18px", display: "inline-flex", alignItems: "center", gap: "6px",
              background: "rgba(247,46,65,0.06)", border: "1px solid rgba(247,46,65,0.2)",
              borderRadius: "999px", padding: "6px 18px",
            }}
          >
            <span style={{ fontSize: "12px" }}>✅</span>
            <span style={{ fontSize: "13px", color: RED_DK, fontWeight: 700, fontFamily: FONT }}>
              Tu plan actual:&nbsp;
              <strong>{TIER_CONFIG[currentTier]?.name || currentTier}</strong>
            </span>
          </div>
        )}

        <div
          style={{
            width: "60px", height: "3px",
            background: `linear-gradient(90deg, ${RED}, #ff6b7a)`,
            borderRadius: "999px", margin: "24px auto 0",
          }}
        />
      </div>

      {/* ── Error ─────────────────────────────────────────────────────── */}
      {error && (
        <div
          style={{
            maxWidth: "460px", margin: "0 auto 36px",
            background: "#fff5f5", border: "1px solid rgba(247,46,65,0.25)",
            borderRadius: "16px", padding: "24px",
            display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", textAlign: "center",
          }}
        >
          <span style={{ fontSize: "36px" }}>⚠️</span>
          <p style={{ color: RED_DK, fontSize: "14px", margin: 0, fontWeight: 600 }}>{error}</p>
          <button
            onClick={refetch}
            style={{
              background: `linear-gradient(135deg, ${RED}, ${RED_DK})`, color: "white",
              border: "none", borderRadius: "10px", padding: "9px 22px",
              fontSize: "13px", fontWeight: 800, cursor: "pointer", fontFamily: FONT,
              boxShadow: "0 4px 12px rgba(247,46,65,0.3)",
            }}
          >
            Reintentar
          </button>
        </div>
      )}

      {/* ── Cards ─────────────────────────────────────────────────────── */}
      <div
        id="plans-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(285px, 1fr))",
          gap: "28px", maxWidth: "1060px", margin: "0 auto", alignItems: "stretch",
        }}
      >
        {isLoading
          ? [0, 1, 2].map((i) => <SkeletonCard key={i} />)
          : plans.map((plan) => (
              <PlanCard
                key={plan.tier}
                plan={plan}
                currentTier={isPending ? null : currentTier}
                onSelect={isPending ? undefined : handleSelectPlan}
                cancellationScheduled={!isFirstTime && cancellationScheduled}
              />
            ))}
      </div>

      {/* ── Footer ────────────────────────────────────────────────────── */}
      {!isLoading && !error && (
        <div style={{ textAlign: "center", marginTop: "52px" }}>
          <div
            style={{
              display: "inline-flex", flexWrap: "wrap",
              justifyContent: "center", gap: "10px", marginBottom: "20px",
            }}
          >
            {[
              { icon: "🔒", text: "Pago seguro" },
              { icon: "🔄", text: "Cancela cuando quieras" },
              { icon: "🎯", text: "Sin permanencia" },
            ].map(({ icon, text }) => (
              <span
                key={text}
                style={{
                  display: "inline-flex", alignItems: "center", gap: "5px",
                  background: "#f8fafc", border: "1px solid #e2e8f0",
                  borderRadius: "999px", padding: "5px 14px",
                  fontSize: "12px", color: "#475569", fontWeight: 600,
                }}
              >
                {icon} {text}
              </span>
            ))}
          </div>
          <p style={{ color: "#94a3b8", fontSize: "13px", margin: 0 }}>
            💬 ¿Tienes preguntas?{" "}
            <a
              href="mailto:soporte@eatery.com"
              style={{ color: RED, fontWeight: 700, textDecoration: "none" }}
            >
              Contáctanos
            </a>{" "}
            y te ayudamos a elegir el plan ideal.
          </p>
        </div>
      )}

      {/* ── Cancellation scheduled banner ─────────────────────────────── */}
      {cancellationScheduled && cancellationDateFormatted && (
        <div
          style={{
            maxWidth: "680px",
            margin: "32px auto 0",
            background: "#fff5f5",
            border: "1.5px solid rgba(247,46,65,0.3)",
            borderRadius: "16px",
            padding: "18px 22px",
            display: "flex",
            alignItems: "flex-start",
            gap: "14px",
            fontFamily: FONT,
          }}
        >
          <span style={{ fontSize: "22px", flexShrink: 0, marginTop: "1px" }}>🚫</span>
          <div>
            <p style={{ margin: "0 0 3px", fontSize: "14px", fontWeight: 800, color: RED_DK }}>
              Cancelación programada
            </p>
            <p style={{ margin: 0, fontSize: "13px", color: RED_DK, opacity: 0.85, lineHeight: 1.55 }}>
              Tu suscripción está activa hasta el <strong>{cancellationDateFormatted}</strong>. Después no se realizarán más cargos.
            </p>
          </div>
        </div>
      )}

      {/* ── Scheduled plan (downgrade) banner ─────────────────────────── */}
      {subscription?.scheduled_plan && (
        <div
          style={{
            maxWidth: '680px', margin: '24px auto 0',
            background: '#fffbeb', border: '1.5px solid #fde68a',
            borderRadius: '16px', padding: '18px 22px',
            display: 'flex', alignItems: 'flex-start', gap: '14px',
            fontFamily: FONT,
          }}
        >
          <span style={{ fontSize: '22px', flexShrink: 0, marginTop: '1px' }}>📅</span>
          <div>
            <p style={{ margin: '0 0 3px', fontSize: '14px', fontWeight: 800, color: '#92400e' }}>
              Cambio de plan programado
            </p>
            <p style={{ margin: 0, fontSize: '13px', color: '#92400e', opacity: 0.9, lineHeight: 1.55 }}>
              Tu suscripción cambiará al plan{' '}
              <strong>
                {TIER_CONFIG[subscription.scheduled_plan.plan_tier]?.name || subscription.scheduled_plan.plan_tier}
              </strong>
              {subscription.scheduled_plan.effective_date
                ? <>{' '}el <strong>{formatUTCDate(subscription.scheduled_plan.effective_date)}</strong></>
                : ' al inicio de tu próximo período'}. Hasta entonces mantienes tu plan actual.
            </p>
          </div>
        </div>
      )}

      {/* ── Downgrade error feedback ──────────────────────────────── */}
      {downgradeError && (
        <>
          <style>{'@keyframes derr-in{0%{opacity:0;transform:translateY(14px)}100%{opacity:1;transform:translateY(0)}}'}</style>
          <div style={{
            position: 'fixed', bottom: '28px', left: '50%', transform: 'translateX(-50%)',
            background: '#fff5f5', border: '1.5px solid rgba(247,46,65,0.28)',
            borderRadius: '16px', padding: '14px 20px',
            maxWidth: '460px', width: 'calc(100% - 48px)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            zIndex: 9999, animation: 'derr-in 0.3s ease both',
            display: 'flex', alignItems: 'flex-start', gap: '10px',
            fontFamily: FONT,
          }}>
            <span style={{ fontSize: '18px', flexShrink: 0, marginTop: '2px' }}>⚠️</span>
            <p style={{ margin: 0, fontSize: '13px', color: RED_DK, fontWeight: 600, flex: 1, lineHeight: 1.5 }}>
              {downgradeError}
            </p>
            <button onClick={() => setDowngradeError(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: RED, opacity: 0.6, flexShrink: 0 }}
            >×</button>
          </div>
        </>
      )}

      {/* ── Cancel error feedback ─────────────────────────────────── */}
      {cancelError && (
        <>
          <style>{'@keyframes cerr-in{0%{opacity:0;transform:translateY(14px)}100%{opacity:1;transform:translateY(0)}}'}</style>
          <div
            style={{
              position: 'fixed', bottom: '28px', left: '50%', transform: 'translateX(-50%)',
              background: '#fff5f5', border: '1.5px solid rgba(247,46,65,0.28)',
              borderRadius: '16px', padding: '14px 20px',
              maxWidth: '460px', width: 'calc(100% - 48px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              zIndex: 9999, animation: 'cerr-in 0.3s ease both',
              display: 'flex', alignItems: 'flex-start', gap: '10px',
              fontFamily: FONT,
            }}
          >
            <span style={{ fontSize: '18px', flexShrink: 0, marginTop: '2px' }}>⚠️</span>
            <p style={{ margin: 0, fontSize: '13px', color: RED_DK, fontWeight: 600, flex: 1, lineHeight: 1.5 }}>
              {cancelError}
            </p>
            <button
              onClick={() => setCancelError(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: RED, opacity: 0.6, flexShrink: 0 }}
            >×</button>
          </div>
        </>
      )}

      {/* ── Subtle cancel link (footer) ─────────────────────────────── */}
      {!isLoading && !error && currentTier && !cancellationScheduled && (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <button
            onClick={() => setCancelModalOpen(true)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#94a3b8', fontSize: '12px', fontWeight: 500,
              fontFamily: FONT, textDecoration: 'underline',
              textDecorationStyle: 'dotted', textUnderlineOffset: '3px',
              transition: 'color 0.2s ease', padding: '4px 8px',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = RED)}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#94a3b8')}
          >
            Deseo cancelar mi suscripción actual
          </button>
        </div>
      )}

      {/* ── First-time Confirmation Modal (Wompi widget) ─────────────────────── */}
      <ConfirmationModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        onConfirm={handleConfirm}
        plan={selectedPlan}
        currentTier={currentTier}
        isLoading={isWompiProcessing}
      />

      {/* ── Upgrade Modal ─────────────────────────────────────────── */}
      <UpgradeModal
        isOpen={upgradeModalOpen}
        onClose={() => !isUpgrading && setUpgradeModalOpen(false)}
        onConfirm={handleUpgrade}
        plan={selectedPlan}
        isLoading={isUpgrading}
      />

      {/* ── Downgrade Modal ───────────────────────────────────────── */}
      <DowngradeModal
        isOpen={downgradeModalOpen}
        onClose={() => !isDowngrading && setDowngradeModalOpen(false)}
        onConfirm={handleDowngrade}
        plan={selectedPlan}
        nextBillingDate={nextBillingDateFormatted}
        isLoading={isDowngrading}
      />

      {/* ── Cancellation Modal ──────────────────────────────────────── */}
      <CancellationModal
        isOpen={cancelModalOpen}
        onClose={() => !isCancelling && setCancelModalOpen(false)}
        onConfirm={handleCancelSubscription}
        currentTier={currentTier}
        isLoading={isCancelling}
      />

      {/* ── Payment Method Modal ────────────────────────────────────────────── */}
      <PaymentMethodModal
        isOpen={paymentMethodModalOpen}
        onClose={() => {
          setPaymentMethodModalOpen(false);
          setPaymentMethodParams(null);
        }}
        onSelect={(method) => {
          setPaymentMethodModalOpen(false);
          openWompiWidget({ ...paymentMethodParams, paymentMethod: method });
          setPaymentMethodParams(null);
        }}
        isLoading={isWompiProcessing}
      />

      {/* ── Wompi Processing Overlay (new subscription tokenizing/polling) */}
      <WompiProcessingOverlay
        widgetState={widgetState}
        errorMsg={errorMsg}
        onRetry={() => { resetWidget(); if (selectedPlan) setModalOpen(true); }}
        onDismissError={() => { resetWidget(); setSelectedPlan(null); }}
      />

      {/* ── Action Overlay (upgrade polling / success / error) ─────────── */}
      <ActionOverlay
        state={overlayState}
        errorMsg={actionError}
        onRetry={() => {
          setOverlayState(null);
          setActionError(null);
          if (selectedPlan) setUpgradeModalOpen(true);
        }}
        onDismiss={() => {
          setOverlayState(null);
          setActionError(null);
          setSelectedPlan(null);
        }}
      />

    </div>
  );
}
